import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { CartContext } from 'context-providers/cart-provider';
import { ProductsContext } from 'context-providers/products-provider';
import {
  getPersistedPromoCode,
  getPromoCodeFromQueryParameters,
} from 'lib/promos';
import { formatMoneyWholeAware } from 'lib/utils/format-money';
import { getFromLocalStorage } from 'lib/utils/get-from-local-storage';
import { setToLocalStorage } from 'lib/utils/set-to-local-storage';

// Referral codes all begin with the "symbiosis" campaign prefix
// (e.g. SYMBIOSIS1-X32XTWT4) — that prefix is the detection signal.
const REFERRAL_CODE_PREFIX = 'symbiosis';

// Referral offer value and currency, set by the Talkable campaign.
const REFERRAL_SAVINGS_USD = 25;
const REFERRAL_SAVINGS_CURRENCY = 'USD';

export const REFERRAL_BANNER_WINDOW_KEY = 'seed_referralBannerWindow';

const WINDOW_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export const isReferralCode = (code?: string | null): boolean =>
  !!code && code.trim().toLowerCase().startsWith(REFERRAL_CODE_PREFIX);

type ReferralBannerWindow = { code: string; expiresAt: number };

const readWindow = (): ReferralBannerWindow | null => {
  const parsed = getFromLocalStorage(REFERRAL_BANNER_WINDOW_KEY);
  if (
    typeof parsed?.code !== 'string' ||
    typeof parsed?.expiresAt !== 'number'
  ) {
    return null;
  }
  return parsed;
};

// Starts the 7-day display window for a referral code. Seeing the same code
// again never restarts the window; a different code replaces it.
export const startReferralBannerWindow = (code: string): void => {
  const existing = readWindow();
  if (existing && existing.code.toUpperCase() === code.trim().toUpperCase()) {
    return;
  }
  setToLocalStorage(
    REFERRAL_BANNER_WINDOW_KEY,
    JSON.stringify({
      code: code.trim(),
      expiresAt: Date.now() + WINDOW_DURATION_MS,
    }),
  );
};

export const isReferralBannerWindowActive = (code: string): boolean => {
  const bannerWindow = readWindow();
  return (
    !!bannerWindow &&
    bannerWindow.code.toUpperCase() === code.trim().toUpperCase() &&
    Date.now() < bannerWindow.expiresAt
  );
};

// Drives the referral site banner. A referred visitor lands with their code
// in the URL; the banner stays up while that code is the persisted promo,
// for at most 7 days.
export const useReferralBanner = () => {
  const { promoCode } = useContext(CartContext);
  const { currencyCode } = useContext(ProductsContext);
  const router = useRouter();
  const [showReferralBanner, setShowReferralBanner] = useState(false);

  useEffect(() => {
    // The URL code shows the banner immediately on landing; the persisted
    // code keeps it up on later visits until a competing promo replaces it.
    const referralCode = [
      getPromoCodeFromQueryParameters(),
      getPersistedPromoCode(),
    ].find(isReferralCode);

    if (!referralCode) {
      setShowReferralBanner(false);
      return;
    }
    startReferralBannerWindow(referralCode);
    setShowReferralBanner(isReferralBannerWindowActive(referralCode));
  }, [promoCode, router.asPath]);

  // The offer is USD-denominated and Shopify converts it at checkout, so
  // non-USD markets see it labeled explicitly as USD rather than converted.
  const usdAmount = formatMoneyWholeAware(
    REFERRAL_SAVINGS_USD,
    REFERRAL_SAVINGS_CURRENCY,
  );
  const referralSavingsLabel =
    currencyCode !== REFERRAL_SAVINGS_CURRENCY
      ? `${REFERRAL_SAVINGS_CURRENCY} ${usdAmount}`
      : usdAmount;

  return { showReferralBanner, referralSavingsLabel };
};
